import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { getStaticPageContent } from "services/getStaticPageContent";
import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "context/languageContext";
import SearchForm from "./SearchForm";
import LoadingPage from "views/LoadingPage";
import defaultImage from "assets/images/staticPageDefaultImage.jpg";
import styles from "./styles.module.scss";
import LandingPageSlider from "views/LandingPage/components/LandingPageSlider";
import { getFilteredPlaces } from "services/getFilteredPlaces";
import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";
import { set } from "date-fns";

const SUPPORTED_CITIES = [
  "paris",
  "lyon",
  "toulouse",
  "bordeaux",
  "strasbourg",
  "lille",
  "marseille",
  "aix-en-provence",
  "clermont-ferrand",
  "limoges",
  "montpellier",
  "nantes",
  "nice",
  "rennes",
  "location-salle-bruxelles",
  "location-salle-liege",
  "location-salle-anvers",
  "location-salle-bruges",
  "location-salle-gand",
  "location-salle-charleroi",
  "location-salle-namur",
  "location-salle-mons",
  "alquiler-sala-madrid",
  "location-salle-bruxelles",
  "alquiler-sala-barcelona",
  "alquiler-sala-sevilla",
  "alquiler-sala-valencia",
  "alquiler-sala-bilbao",
  "alquiler-sala-alicante",
  "alquiler-sala-malaga",
  "alquiler-sala-zaragoza",
  "alquiler-sala-cordoba",
  "alquiler-sala-valladolid",
  "alquiler-sala-pamplona",
  "alquiler-sala-marbella",
  "alquiler-sala-granada",
];

const GENERAL_PAGE_KEYS = ["qui-sommes-nous", "cgu", "press", "plan", "mentions-legales", "politique-confidentialite"];

const StaticPageMain = () => {
  const navigate = useNavigate();
  const { pageKey } = useParams();
  const [content, setContent] = useState(null);
  const { language } = useLanguage();
  const [firstImage, setFirstImage] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const [frontendDescription, setFrontendDescription] = useState("");
  const [pageMetaTitle, setPageMetaTitle] = useState("");
  const [pageMetaDescription, setPageMetaDescription] = useState("");
  const [pageMetaKeywords, setPageMetaKeywords] = useState("");
  const [dynamicSliderSpaces, setDynamicSliderSpaces] = useState([]);
  const [hasDynamicSlider, setHasDynamicSlider] = useState(false);
  const [processedContent, setProcessedContent] = useState("");
  const [sliderContainer, setSliderContainer] = useState(null);
  const { t } = useTranslation("global");

  const [h3WithUlSections, setH3WithUlSections] = useState([]);

  const isFollowedByUl = useCallback((node) => {
    let next = node.nextElementSibling;
    while (next && next.nodeName === "LINK") {
      next = next.nextElementSibling;
    }
    return next?.nodeName === "UL";
  }, []);

  const handleStaticCarousel = useCallback(async (match, body) => {
    try {
      const [usage, style, city, postalCode, country] = match
        .replace(/{{|}}/g, "")
        .split("|");

      const data = await getFilteredPlaces({
        usages: [usage],
        style,
        city,
        postalCode,
        country,
        lang: language,
      });
      setDynamicSliderSpaces(data.data);
      setHasDynamicSlider(true);

      return body.replace(match, "<div id='static-page-dynamic-slider'></div>");
    } catch (error) {
      return body.replace(match, "");
    }
  }, []);

  const processDynamicContent = useCallback(
    async (body) => {
      let processedBody = body;

      const dynamicMatch = processedBody.match(/\{\{([^}]+)\}\}/);
      if (dynamicMatch) {
        processedBody = await handleStaticCarousel(
          dynamicMatch[0],
          processedBody
        );
      }

      return processedBody;
    },
    [handleStaticCarousel]
  );

  const processTableElements = useCallback(
    (doc) => {
      doc.querySelectorAll('[class*="tableContainer"]').forEach((wrapper) => {
        const table = wrapper.querySelector("table");
        if (table) {
          wrapper.parentNode.insertBefore(table, wrapper);
          wrapper.remove();
        }
      });

      doc.querySelectorAll("table").forEach((table) => {
        const wrapper = doc.createElement("div");
        wrapper.className = styles.tableContainer;

        table.removeAttribute("style");
        table.removeAttribute("width");
        table.style.width = "100%";
        table.style.minWidth = "100%";

        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      });
    },
    [styles.tableContainer]
  );

  const extractH3WithUlSections = useCallback(
    (doc) => {
      const h3Elements = Array.from(doc.querySelectorAll("h3"));
      const extractedSections = [];

      h3Elements.forEach((h3) => {
        let nextElement = h3.nextElementSibling;

        while (nextElement && nextElement.nodeName === "LINK") {
          nextElement = nextElement.nextElementSibling;
        }

        if (nextElement?.nodeName === "UL") {
          const container = doc.createElement("div");
          container.className = styles.h3WithUlContainer;

          const titleContainer = doc.createElement("h3");
          titleContainer.className = styles.h3Title;
          titleContainer.innerHTML = h3.innerHTML;

          const listContainer = doc.createElement("div");
          listContainer.className = styles.ulContainer;
          listContainer.appendChild(nextElement.cloneNode(true));

          container.appendChild(titleContainer);
          container.appendChild(listContainer);

          extractedSections.push(container.outerHTML);

          nextElement.remove();
          h3.remove();
        }
      });

      return extractedSections;
    },
    [styles.h3WithUlContainer, styles.h3Title, styles.ulContainer]
  );

  const processVerticalSections = useCallback(
    (doc) => {
      const headingElements = Array.from(doc.querySelectorAll("h1, h2"));

      headingElements.forEach((heading) => {
        const sectionContainer = doc.createElement("div");
        sectionContainer.className = styles.verticalSectionContainer;

        const titleContainer = heading.nodeName === "H1" ? doc.createElement("h1") : doc.createElement("h2");

        if (heading.nodeName === "H1") {
          titleContainer.className = `${styles.verticalSectionTitle} ${styles.verticalSectionTitleH1}`;
        } else if (heading.nodeName === "H2") {
          titleContainer.className = `${styles.verticalSectionTitle} ${styles.verticalSectionTitleH2}`;
        }

        titleContainer.innerHTML = heading.innerHTML;

        const contentContainer = doc.createElement("div");
        contentContainer.className = styles.verticalSectionContent;

        let nextNode = heading.nextSibling;
        while (nextNode) {
          if (
            ["H1", "H2"].includes(nextNode.nodeName) ||
            (nextNode.nodeName === "H3" && isFollowedByUl(nextNode))
          ) {
            break;
          }

          const currentNode = nextNode;
          nextNode = nextNode.nextSibling;
          contentContainer.appendChild(currentNode.cloneNode(true));
          currentNode.remove();
        }

        sectionContainer.appendChild(titleContainer);
        sectionContainer.appendChild(contentContainer);
        heading.parentNode.replaceChild(sectionContainer, heading);
      });
    },
    [
      isFollowedByUl,
      styles.verticalSectionContainer,
      styles.verticalSectionTitle,
      styles.verticalSectionTitleH1,
      styles.verticalSectionTitleH2,
      styles.verticalSectionContent,
    ]
  );

  const getFormattedCityName = useCallback((city) => {
    if (!city || typeof city !== "string") return "";
    return city.charAt(0).toUpperCase() + city.slice(1);
  }, []);

  const loadPageContent = useCallback(async () => {
    try {
      const pageData = await getStaticPageContent(pageKey, language);
      if (!pageData) throw new Error("Page content not found");

      let processedBody = await processDynamicContent(pageData.body);

      const parser = new DOMParser();
      const doc = parser.parseFromString(processedBody, "text/html");

      doc.querySelectorAll("p, em, span").forEach((el) => {
        const isEffectivelyEmpty =
          el.innerHTML.trim() === "" ||
          el.innerHTML.trim() === "&nbsp;" ||
          el.innerHTML.replace(/<br>/g, "").trim() === "" ||
          el.innerHTML.replace(/<br\s?\/?>/g, "").trim() === "&nbsp;";

        if (isEffectivelyEmpty) {
          el.remove();
        }
      });

      processTableElements(doc);

      const unwantedH1 = doc
        .querySelector('h1 strong span[style*="background-color:#d51b81"]')
        ?.closest("h1");
      if (unwantedH1) {
        unwantedH1.remove();
      }

      const extractedSections = extractH3WithUlSections(doc);
      setH3WithUlSections(extractedSections);

      processVerticalSections(doc);

      setContent({ ...pageData, body: doc.body.innerHTML });
      setProcessedContent(doc.body.innerHTML);
      setFirstImage(pageData.image_url || "");
      setPageTitle(pageData.title);
      setFrontendDescription(pageData.frontend_description || "");
      setPageMetaTitle(pageData.meta_title);
      setPageMetaDescription(pageData.meta_description);
      setPageMetaKeywords(pageData.meta_keywords);
    } catch (error) {
      navigate("/notfound", { replace: true });
    }
  }, [
    pageKey,
    navigate,
    language,
    processDynamicContent,
    processTableElements,
    extractH3WithUlSections,
    processVerticalSections,
  ]);

  useEffect(() => {
    loadPageContent();
  }, [loadPageContent]);

  useEffect(() => {
    const sliderEl = document.getElementById("static-page-dynamic-slider");
    if (sliderEl) {
      setSliderContainer(sliderEl);
    }
  }, [processedContent]);

  useEffect(() => {
    if (!content) return;

    const timer = setTimeout(() => {
      const container = document.getElementById("static-content-container");
      if (!container) return;

      const scriptElements = container.querySelectorAll("script");

      scriptElements.forEach((oldScript) => {
        const newScript = document.createElement("script");
        newScript.type = oldScript.type || "text/javascript";

        if (oldScript.src) {
          newScript.src = oldScript.src;
          newScript.async = true;
        } else {
          newScript.textContent = oldScript.textContent;
        }

        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [content]);

  // Determine SEO values
  const seoTitle = pageMetaTitle || pageTitle || "Choose&Work";
  const seoDescription = pageMetaDescription || "Meeting rooms, private offices, coworking spaces and virtual offices. Find your perfect workspace with Choose&Work.";
  const seoKeywords = pageMetaKeywords || "coworking, meeting rooms, private offices, virtual offices, workspace";
  
  // Get current canonical URL
  const currentPath = window.location.pathname;
  const baseUrl = "https://chooseandwork.com";
  const canonicalUrl = `${baseUrl}${currentPath}`;

  if (!content) {
    return <LoadingPage />;
  }

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <link rel="canonical" href={canonicalUrl} />
          <meta name="robots" content="index, follow" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={language === 'fr' ? 'fr_FR' : 'en_US'} />
        {firstImage && <meta property="og:image" content={firstImage} />}
        
        {/* Twitter Card */}
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:card" content="summary_large_image" />
        {firstImage && <meta name="twitter:image" content={firstImage} />}
      </Helmet>

      <section className={`${styles.staticPage} mediumContainer`}>
        {!GENERAL_PAGE_KEYS.includes(pageKey?.toLowerCase()) && (
          <div className={styles.topSection}>
            <div className={styles.searchFormContainer}>
              <p
                className={styles.searchFormHeader}
                dangerouslySetInnerHTML={{ __html: pageTitle }}
              ></p>
              {/* <p className={styles.searchFormSubHeader}>{pageMetaTitle}</p> */}
              <p className={styles.searchFormText}>{frontendDescription}</p>
              <SearchForm />
            </div>
            <div
              className={styles.topImage}
              style={{
                backgroundImage: `url(${firstImage || defaultImage})`,
              }}
            />
          </div>
        )}

        <div className={styles.row}>
          <div
            id="static-content-container"
            className={styles.contentWrapper}
            dangerouslySetInnerHTML={{ __html: processedContent }}
          />
        </div>

        {/* Dynamic slider rendered via portal */}
        {sliderContainer &&
          hasDynamicSlider &&
          dynamicSliderSpaces.length > 0 &&
          ReactDOM.createPortal(
            <div className={styles.dynamicSliderWrapper}>
              <h3 className={styles.sliderHeader}>
                {SUPPORTED_CITIES.includes(pageKey?.toLowerCase())
                  ? t("contactUs.newSpacesSubtitle", {
                      pageKey: getFormattedCityName(pageKey),
                    })
                  : t("contactUs.newSpacesSubtitleAllLocations")}
              </h3>
              <LandingPageSlider spaces={dynamicSliderSpaces} />
            </div>,
            sliderContainer
          )}
        {h3WithUlSections.length > 0 && (
          <div className={styles.h3WithUlSectionsWrapper}>
            {h3WithUlSections.map((sectionHtml, index) => (
              <div
                key={index}
                className={styles.h3WithUlSection}
                dangerouslySetInnerHTML={{ __html: sectionHtml }}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
};

export default StaticPageMain;